import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { LeadershipTermsManageDialog } from '@/components/shared/leadership-terms-manage-dialog';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

const CUSTOM_ROLE = '__custom__';

interface UnitTerm {
  id: string;
  startYear: number;
  endYear: number | null;
  label: string | null;
  isCurrent: boolean;
  officeCount: number;
}

interface UnitOffice {
  id: string;
  memberId: string;
  memberName: string;
  roleCode: string | null;
  roleName: string;
}

interface OfficerRole {
  code: string;
  label: string;
  sortOrder: number;
}

interface DepartmentUnitLeadershipPanelProps {
  departmentId: string;
  unitId: string;
  canManage: boolean;
}

const formatTermYears = (term: Pick<UnitTerm, 'startYear' | 'endYear'>): string => {
  if (term.endYear == null || term.startYear === term.endYear) {
    return String(term.startYear);
  }
  return `${term.startYear}–${term.endYear}`;
};

const formatTermLabel = (
  term: Pick<UnitTerm, 'startYear' | 'endYear' | 'label' | 'isCurrent'>,
  currentBadge: string,
): string => {
  const years = term.label?.trim() || formatTermYears(term);
  return term.isCurrent ? `${years} (${currentBadge})` : years;
};

export const DepartmentUnitLeadershipPanel = ({
  departmentId,
  unitId,
  canManage,
}: DepartmentUnitLeadershipPanelProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const basePath = `/people/departments/${departmentId}/units/${unitId}`;

  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [termsManageOpen, setTermsManageOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [editingTermId, setEditingTermId] = useState<string | null>(null);
  const [officeOpen, setOfficeOpen] = useState(false);
  const [removeOfficeId, setRemoveOfficeId] = useState<string | null>(null);
  const [startYear, setStartYear] = useState(String(new Date().getFullYear()));
  const [endYear, setEndYear] = useState(String(new Date().getFullYear()));
  const [termLabel, setTermLabel] = useState('');
  const [termIsCurrent, setTermIsCurrent] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [roleSelection, setRoleSelection] = useState<string | null>(null);
  const [customRoleName, setCustomRoleName] = useState('');

  const terms = useQuery({
    queryKey: ['people', 'departments', departmentId, 'units', unitId, 'terms'],
    queryFn: () => api.get<{ data: UnitTerm[] }>(`${basePath}/terms?pageSize=100`),
  });

  const roles = useQuery({
    queryKey: ['people', 'departments', 'units', 'officer-roles'],
    queryFn: () => api.get<{ data: OfficerRole[] }>('/people/departments/units/officer-roles'),
  });

  const unitMembers = useQuery({
    queryKey: ['people', 'departments', departmentId, 'units', unitId, 'members'],
    queryFn: () =>
      api.get<{ data: Array<{ memberId: string; firstName: string; lastName: string }> }>(
        `${basePath}/members?pageSize=100`,
      ),
  });

  const termRows = terms.data?.data ?? [];

  useEffect(() => {
    if (selectedTermId && termRows.some((term) => term.id === selectedTermId)) return;
    const current = termRows.find((term) => term.isCurrent);
    setSelectedTermId(current?.id ?? termRows[0]?.id ?? null);
  }, [selectedTermId, termRows]);

  const offices = useQuery({
    queryKey: ['people', 'departments', departmentId, 'units', unitId, 'terms', selectedTermId, 'offices'],
    queryFn: () =>
      api.get<{ data: UnitOffice[] }>(
        `${basePath}/terms/${selectedTermId}/offices?pageSize=100`,
      ),
    enabled: Boolean(selectedTermId),
  });

  useEffect(() => {
    if (!termOpen) return;
    if (editingTermId) {
      const term = termRows.find((row) => row.id === editingTermId);
      if (!term) return;
      setStartYear(String(term.startYear));
      setEndYear(term.endYear != null ? String(term.endYear) : String(term.startYear));
      setTermLabel(term.label ?? '');
      setTermIsCurrent(term.isCurrent);
      return;
    }
    const year = String(new Date().getFullYear());
    setStartYear(year);
    setEndYear(year);
    setTermLabel('');
    setTermIsCurrent(!termRows.some((term) => term.isCurrent));
  }, [termOpen, editingTermId, termRows]);

  useEffect(() => {
    if (!officeOpen) return;
    setMemberId(null);
    setRoleSelection(null);
    setCustomRoleName('');
  }, [officeOpen]);

  const invalidateTerms = () => {
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'units', unitId, 'terms'],
    });
    void queryClient.refetchQueries({
      queryKey: ['people', 'departments', departmentId, 'units'],
    });
  };

  const openCreateTerm = () => {
    setEditingTermId(null);
    setTermOpen(true);
  };

  const openEditTerm = (termId?: string) => {
    const id = termId ?? selectedTermId;
    if (!id) return;
    setSelectedTermId(id);
    setEditingTermId(id);
    setTermOpen(true);
  };

  const openCreateTermFromManage = () => {
    setTermsManageOpen(false);
    openCreateTerm();
  };

  const openEditTermFromManage = (termId: string) => {
    setTermsManageOpen(false);
    openEditTerm(termId);
  };

  const closeTermDialog = () => {
    setTermOpen(false);
    setEditingTermId(null);
  };

  const saveTerm = useMutation({
    mutationFn: () => {
      const body = {
        startYear: Number(startYear),
        endYear: termIsCurrent ? null : Number(endYear),
        label: termLabel.trim() || null,
        isCurrent: termIsCurrent,
      };
      if (editingTermId) {
        return api.patch<UnitTerm>(`${basePath}/terms/${editingTermId}`, body);
      }
      return api.post<UnitTerm>(`${basePath}/terms`, body);
    },
    onSuccess: (term) => {
      toast.success(t('departments.units.leadership.termSaved'));
      closeTermDialog();
      setSelectedTermId(term.id);
      invalidateTerms();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const assignOffice = useMutation({
    mutationFn: () => {
      if (!selectedTermId || !memberId || !roleSelection) {
        throw new Error('incomplete');
      }
      const isCustom = roleSelection === CUSTOM_ROLE;
      return api.post(`${basePath}/terms/${selectedTermId}/offices`, {
        memberId,
        roleCode: isCustom ? null : roleSelection,
        roleName: isCustom ? customRoleName.trim() : null,
      });
    },
    onSuccess: () => {
      toast.success(t('departments.units.leadership.officeSaved'));
      setOfficeOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['people', 'departments', departmentId, 'units', unitId, 'terms', selectedTermId, 'offices'],
      });
      invalidateTerms();
      void queryClient.invalidateQueries({
        queryKey: ['people', 'departments', departmentId, 'units', unitId, 'members'],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeOffice = useMutation({
    mutationFn: (officeId: string) => api.delete(`${basePath}/offices/${officeId}`),
    onSuccess: () => {
      toast.success(t('departments.units.leadership.officeRemoved'));
      setRemoveOfficeId(null);
      void queryClient.invalidateQueries({
        queryKey: ['people', 'departments', departmentId, 'units', unitId, 'terms', selectedTermId, 'offices'],
      });
      invalidateTerms();
      void queryClient.invalidateQueries({
        queryKey: ['people', 'departments', departmentId, 'units', unitId, 'members'],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const roleOptions = useMemo(
    () => [
      ...(roles.data?.data ?? []).map((role) => ({
        value: role.code,
        label: t(`departments.units.leadership.roles.${role.code}`, { defaultValue: role.label }),
      })),
      { value: CUSTOM_ROLE, label: t('departments.units.leadership.customRole') },
    ],
    [roles.data?.data, t],
  );

  const memberOptions = useMemo(
    () =>
      (unitMembers.data?.data ?? []).map((member) => ({
        value: member.memberId,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [unitMembers.data?.data],
  );

  const columns: DataTableColumn<UnitOffice>[] = [
    {
      id: 'role',
      header: t('departments.units.leadership.columns.position'),
      cell: (row) => row.roleName,
    },
    {
      id: 'name',
      header: t('departments.units.leadership.columns.person'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    ...(canManage
      ? [
          {
            id: 'actions',
            header: tCommon('table.actions'),
            align: 'right' as const,
            cell: (row: UnitOffice) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  setRemoveOfficeId(row.id);
                }}
              >
                {tCommon('action.remove')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  const selectedTerm = termRows.find((term) => term.id === selectedTermId) ?? null;
  const hasCurrentTerm = termRows.some((term) => term.isCurrent);
  const blockCurrentOnCreate = termOpen && !editingTermId && hasCurrentTerm;
  const canAssign =
    canManage &&
    Boolean(selectedTermId) &&
    Boolean(memberId) &&
    Boolean(roleSelection) &&
    (roleSelection !== CUSTOM_ROLE || customRoleName.trim().length > 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label>{t('departments.units.leadership.term')}</Label>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full min-w-[9rem] max-w-[12rem]">
            <Listbox
              value={selectedTermId}
              onChange={setSelectedTermId}
              options={termRows.map((term) => ({
                value: term.id,
                label: formatTermLabel(term, t('departments.units.leadership.currentTermBadge')),
                description:
                  term.label?.trim() && !term.isCurrent ? formatTermYears(term) : undefined,
              }))}
              placeholder={t('departments.units.leadership.termPlaceholder')}
              emptyMessage={t('departments.units.leadership.noTerms')}
            />
          </div>

          {!selectedTerm ? (
            <>
              <p className="min-w-0 flex-1 text-body-sm text-fg-secondary">
                {t('departments.units.leadership.emptyTerms')}
              </p>
              {canManage ? (
                <Button size="sm" variant="outline" onClick={() => setTermsManageOpen(true)}>
                  {t('departments.units.leadership.manageTerms')}
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <h4 className="min-w-0 flex-1 text-body-sm font-medium text-fg">
                {t('departments.units.leadership.positionsTitle')}
              </h4>
              {canManage ? (
                <div className="flex shrink-0 flex-wrap justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setTermsManageOpen(true)}>
                    {t('departments.units.leadership.manageTerms')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setOfficeOpen(true)}>
                    {t('departments.units.leadership.setPositions')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {selectedTerm ? (
        <DataTable
          rows={offices.data?.data ?? []}
          columns={columns}
          rowKey={(row) => row.id}
          loading={offices.isLoading}
          empty={
            <p className="text-body-sm text-fg-secondary">
              {t('departments.units.leadership.emptyOffices')}
            </p>
          }
        />
      ) : null}

      <LeadershipTermsManageDialog
        open={termsManageOpen}
        onOpenChange={setTermsManageOpen}
        terms={termRows}
        selectedTermId={selectedTermId}
        title={t('departments.units.leadership.manageTerms')}
        emptyMessage={t('departments.units.leadership.emptyTerms')}
        addTermLabel={t('departments.units.leadership.addTerm')}
        editTermLabel={t('departments.units.leadership.editTerm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        tipId="groups.term"
        formatTermLabel={(term) =>
          formatTermLabel(term, t('departments.units.leadership.currentTermBadge'))
        }
        formatTermYears={formatTermYears}
        onAddTerm={openCreateTermFromManage}
        onEditTerm={openEditTermFromManage}
      />

      <Dialog
        open={termOpen}
        onOpenChange={(open) => {
          if (!open) closeTermDialog();
          else setTermOpen(true);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editingTermId
                ? t('departments.units.leadership.editTerm')
                : t('departments.units.leadership.addTerm')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              saveTerm.mutate();
            }}
          >
            <DialogBody>
              <div className="flex flex-col gap-3">
                <Tip tipId="groups.term" />
                <div className={`grid gap-3 ${termIsCurrent ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="unit-term-start">{t('departments.units.leadership.startYear')}</Label>
                    <Input
                      id="unit-term-start"
                      type="number"
                      inputMode="numeric"
                      min={1900}
                      max={2100}
                      required
                      value={startYear}
                      onChange={(event) => setStartYear(event.target.value)}
                    />
                  </div>
                  {!termIsCurrent ? (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="unit-term-end">{t('departments.units.leadership.endYear')}</Label>
                      <Input
                        id="unit-term-end"
                        type="number"
                        inputMode="numeric"
                        min={1900}
                        max={2100}
                        required
                        value={endYear}
                        onChange={(event) => setEndYear(event.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="unit-term-label">{t('departments.units.leadership.termLabel')}</Label>
                  <Input
                    id="unit-term-label"
                    value={termLabel}
                    onChange={(event) => setTermLabel(event.target.value)}
                    placeholder={t('departments.units.leadership.termLabelPlaceholder')}
                  />
                </div>
                <SwitchField
                  id="unit-term-current"
                  label={t('departments.units.leadership.currentTermLabel')}
                  description={
                    blockCurrentOnCreate
                      ? t('departments.units.leadership.currentTermAddBlockedHint')
                      : t('departments.units.leadership.currentTermHint')
                  }
                  checked={termIsCurrent}
                  disabled={blockCurrentOnCreate}
                  onCheckedChange={(checked) => {
                    setTermIsCurrent(checked);
                    if (!checked) {
                      setEndYear((current) => current || startYear);
                    }
                  }}
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeTermDialog}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={saveTerm.isPending}>
                {t('departments.units.leadership.saveTerm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={officeOpen} onOpenChange={setOfficeOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('departments.units.leadership.assign')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canAssign) return;
              assignOffice.mutate();
            }}
          >
            <DialogBody className="overflow-visible">
              <div className="flex flex-col gap-3">
                {selectedTerm ? (
                  <p className="text-body-sm text-fg-secondary">
                    {t('departments.units.leadership.assignHint', {
                      term: selectedTerm.label?.trim() || formatTermYears(selectedTerm),
                    })}
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Label>{t('departments.units.leadership.position')}</Label>
                  <Listbox
                    value={roleSelection}
                    onChange={setRoleSelection}
                    options={roleOptions}
                    placeholder={t('departments.units.leadership.positionPlaceholder')}
                    searchable
                    popoverPortal="dialog"
                  />
                </div>
                {roleSelection === CUSTOM_ROLE ? (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="unit-custom-role">
                      {t('departments.units.leadership.customRoleName')}
                    </Label>
                    <Input
                      id="unit-custom-role"
                      value={customRoleName}
                      onChange={(event) => setCustomRoleName(event.target.value)}
                      required
                    />
                  </div>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Label>{t('departments.units.leadership.person')}</Label>
                  <Listbox
                    value={memberId}
                    onChange={setMemberId}
                    options={memberOptions}
                    placeholder={t('departments.units.leadership.personPlaceholder')}
                    searchable
                    emptyMessage={t('departments.units.leadership.noMembers')}
                    popoverPortal="dialog"
                  />
                </div>
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOfficeOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={assignOffice.isPending} disabled={!canAssign}>
                {t('departments.units.leadership.saveOffice')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removeOfficeId)}
        onOpenChange={(open) => {
          if (!open) setRemoveOfficeId(null);
        }}
        title={t('departments.units.leadership.removeTitle')}
        description={t('departments.units.leadership.removeDescription')}
        confirmLabel={tCommon('action.remove')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeOffice.isPending}
        onConfirm={() => {
          if (removeOfficeId) removeOffice.mutate(removeOfficeId);
        }}
      />
    </div>
  );
};
